import type { Tool } from "@repo/app-types/entities/tool";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { Link } from "@repo/ui/Link/routerLink";
import { SlideoverContainer } from "@repo/ui/Slideover/slideoverContainer";
import { IconExternalLink } from "@tabler/icons-react";
import { type FC, useCallback, useMemo, useState } from "react";
import { Button } from "react-aria-components";
import { useNavigate } from "react-router";
import { useAllToolsQuery } from "../../../api/entities/tool.js";
import { appPaths } from "../../../app/appPaths.js";
import { AddToolMenu } from "./addToolMenu.js";
import { ToolEditorSlideoverDialog } from "./toolEditorSlideoverDialog.js";
import { ToolsTable } from "./toolsTable.js";

export interface ToolsSectionProps {
  /** IDs of tools currently assigned to the agent */
  toolIds: readonly string[];
  /** Whether the tools have unpublished changes */
  hasUnpublishedChanges?: boolean;
  /** Callback when tool IDs change */
  onToolIdsChange: (toolIds: readonly string[]) => void;
  /** Callback to publish the agent after tool changes */
  onPublish?: () => Promise<void>;
  /** Whether the section is disabled */
  isDisabled?: boolean;
}

export const ToolsSection: FC<ToolsSectionProps> = ({
  toolIds,
  hasUnpublishedChanges,
  onToolIdsChange,
  onPublish,
  isDisabled,
}) => {
  const { data: allTools, isFetching: isToolsLoading } = useAllToolsQuery();
  const navigate = useNavigate();
  const [editingToolId, setEditingToolId] = useState<string | null>(null);

  // Get the actual Tool objects for the assigned tool IDs
  const assignedTools = useMemo(() => {
    if (!allTools) return [];
    return toolIds
      .map((id) => allTools.find((tool) => tool.id === id))
      .filter((tool): tool is Tool => tool !== undefined);
  }, [toolIds, allTools]);

  // Get tools that are not yet assigned to this agent
  const availableTools = useMemo(() => {
    if (!allTools) return [];
    return allTools.filter((tool) => !toolIds.includes(tool.id));
  }, [allTools, toolIds]);

  const handleToolClick = useCallback((tool: Tool) => {
    setEditingToolId(tool.id);
  }, []);

  const handleSelectTool = useCallback(
    (tool: Tool) => {
      onToolIdsChange([...toolIds, tool.id]);
    },
    [toolIds, onToolIdsChange],
  );

  // Empty state when no tools exist at all
  if (!isToolsLoading && allTools?.length === 0) {
    return (
      <div className="w-full">
        <Label size={Size.SMALL} className="mb-1.5">
          <span className="flex items-center gap-1.5">
            Tools
            {hasUnpublishedChanges && (
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
            )}
          </span>
        </Label>
        <div className="text-sm text-gray-600">
          Configure{" "}
          <Button
            className="cursor-pointer font-medium text-blue-600 hover:text-blue-500"
            onClick={() => {
              window.open(appPaths.agentTools.build(), "_blank");
            }}
          >
            tools{" "}
            <IconExternalLink className="mb-1 inline size-4 align-middle" />
          </Button>{" "}
          to enable capabilities like call transfer, booking appointments, and
          more.
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <Label size={Size.SMALL} className="mb-1.5">
        <span className="flex items-center gap-1.5">
          Tools
          {hasUnpublishedChanges && (
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
          )}
        </span>
      </Label>

      <div className="mt-2">
        {assignedTools.length > 0 && (
          <div className="mb-2">
            <ToolsTable
              tools={assignedTools}
              isLoading={isToolsLoading}
              onToolClick={handleToolClick}
            />
          </div>
        )}

        <AddToolMenu
          availableTools={availableTools}
          onSelectTool={handleSelectTool}
          isDisabled={isDisabled || isToolsLoading}
        />

        {assignedTools.length === 0 && availableTools.length > 0 ? (
          <p className="mt-2 text-sm text-gray-600">
            Add tools to give your agent the ability to transfer calls, book
            appointments, and more.
          </p>
        ) : null}

        {availableTools.length === 0 && allTools && allTools.length === 0 ? (
          <p className="mt-2 text-sm text-gray-600">
            Give your agent capabilities like transferring calls or booking
            appointments.{" "}
            <Link
              href={appPaths.agentTools.build()}
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              Create a tool
            </Link>{" "}
            to get started.
          </p>
        ) : null}

        {availableTools.length === 0 && allTools && allTools.length > 0 ? (
          <p className="mt-2 text-sm text-gray-600">
            All tools have been added.{" "}
            <Link
              href={appPaths.agentTools.build()}
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              Manage tools
            </Link>{" "}
            to create new ones.
          </p>
        ) : null}
      </div>

      {/* Edit Tool Slideover */}
      <SlideoverContainer
        isOpen={editingToolId !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setEditingToolId(null);
          }
        }}
        size={Size.SMALL}
        isDismissable
      >
        {editingToolId ? (
          <ToolEditorSlideoverDialog
            toolId={editingToolId}
            onRemoveFromAgent={(toolId) => {
              onToolIdsChange(toolIds.filter((id) => id !== toolId));
            }}
            onAfterSave={onPublish}
          />
        ) : null}
      </SlideoverContainer>
    </div>
  );
};
